import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { WaitingSpinner, QuestionFeedbackSection, GameEndedScreen, getPlayerHeaderLeftButton } from '../shared';
import { useHostPlaying } from '../../hooks/useHostPlaying';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { calculateTimeRemaining } from '../../utils/gameUtils';
import { isAvatarImage } from '../../utils/avatarUtils';
import type { GameStatistics } from '../../types/game';
import '../shared/TimerBar.css';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';
import { partyPinkGameTheme } from '../../themes/cssTheme';

interface DrinkingGameQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  correctAnswer?: string;
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
  sipPenalty?: number;
  funFact?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  isDrinkingGame?: boolean;
}

interface SipResultEntry {
  playerId: string;
  playerName: string;
  avatar?: string;
  sips: number;
  wrongAnswers?: number;
}

type QuestionResultPayload = {
  sipResults?: SipResultEntry[];
  sipPenalty?: number;
  submissions?: Array<{
    playerId: string;
    playerName?: string;
    isCorrect: boolean;
  }>;
  leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
};

const renderAvatar = (avatar?: string, size: string = '1.5rem') => {
  if (isAvatarImage(avatar)) {
    return <img src={avatar} alt="" style={{ width: size, height: size, objectFit: 'contain' }} />;
  }
  return <span style={{ fontSize: size }}>{avatar || '👤'}</span>;
};

/** Prefer server sipResults; fall back to wrong submissions so host-as-player always has a roster. */
function resolveSipResults(
  data: QuestionResultPayload | null | undefined,
  players: Array<{ id: string; name: string; avatar?: string }>,
  fallbackSipPenalty: number
): SipResultEntry[] {
  const raw = data?.sipResults;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((entry) => {
      const player = players.find((p) => p.id === entry.playerId);
      return {
        playerId: entry.playerId,
        playerName: entry.playerName || player?.name || 'Player',
        avatar: entry.avatar ?? player?.avatar,
        sips: entry.sips > 0 ? entry.sips : (data?.sipPenalty || fallbackSipPenalty || 1),
        wrongAnswers: entry.wrongAnswers,
      };
    });
  }

  const submissions = data?.submissions;
  if (!Array.isArray(submissions) || submissions.length === 0) return [];

  const penalty = data?.sipPenalty || fallbackSipPenalty || 1;
  return submissions
    .filter((s) => s && s.isCorrect === false)
    .map((s) => {
      const player = players.find((p) => p.id === s.playerId);
      return {
        playerId: s.playerId,
        playerName: s.playerName || player?.name || 'Player',
        avatar: player?.avatar,
        sips: penalty,
      };
    });
}

interface DrinkingGamePlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: DrinkingGameQuestion;
    finalLeaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    gameStatistics?: GameStatistics | null;
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

// Theme colors for Drinking Game — free-room pink palette; branding overrides
const THEME = partyPinkGameTheme;


// Phase after answering: waiting for others -> drinking time -> loading next
type WaitingPhase = 'waiting' | 'drinking' | 'loading';

const DrinkingGamePlayerDisplay: React.FC<DrinkingGamePlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const hostIsPlaying = useHostPlaying(roomState);
  const [currentQuestion, setCurrentQuestion] = useState<DrinkingGameQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [myScore, setMyScore] = useState(0);
  const [sipsEarned, setSipsEarned] = useState(0);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [showInterestingFact, setShowInterestingFact] = useState(false);
  const [waitingPhase, setWaitingPhase] = useState<WaitingPhase>('waiting');
  const [sipResults, setSipResults] = useState<SipResultEntry[]>([]);
  const isSubmittingRef = useRef<boolean>(false);
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const currentQuestionIdRef = useRef<string | undefined>(undefined);
  const currentQuestionRef = useRef<DrinkingGameQuestion | null>(null);
  const playersRef = useRef(roomState.players);
  playersRef.current = roomState.players;
  const gameEndData = useGameEndScreenData(roomState);

  // Helper to reset question state
  const resetQuestion = () => {
    setSelectedAnswer(null);
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setSipsEarned(0);
    setPointsEarned(0);
    setShowInterestingFact(false);
    setWaitingPhase('waiting');
    setSipResults([]);
    isSubmittingRef.current = false;
  };

  // Update my score from room state
  useEffect(() => {
    const player = roomState.players.find((p) => p.id === myPlayerId);
    if (player) {
      setMyScore(player.score);
    }
  }, [roomState.players, myPlayerId]);

  // Recover end screen after remount / missed game-ended (e.g. tab sleep)
  const roomGameState = roomState.gameState;
  useEffect(() => {
    if (roomGameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- gameEndData is derived from roomState
  }, [roomGameState, gameEndData]);

  // Handle question change and sync timer from roomState (e.g. after tab focus)
  useEffect(() => {
    if (roomGameState === 'finished') return;
    if (!roomState.currentQuestion) return;
    const questionId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
    const questionStartTime = (roomState as any).questionStartTime;
    const serverTimeRemaining = calculateTimeRemaining(roomState.currentQuestion, questionStartTime);
    const isNewQuestion = previousQuestionIdRef.current !== questionId;
    if (isNewQuestion && questionId) {
      console.log(`📊 Question changed: ${previousQuestionIdRef.current} -> ${questionId}`);
      setCurrentQuestion(roomState.currentQuestion);
      currentQuestionRef.current = roomState.currentQuestion;
      setTimeRemaining(serverTimeRemaining);
      resetQuestion();
      previousQuestionIdRef.current = questionId;
      currentQuestionIdRef.current = questionId;
    } else if (questionId && previousQuestionIdRef.current === questionId) {
      setTimeRemaining(serverTimeRemaining);
    }
  }, [roomState.currentQuestion, (roomState as any).questionStartTime, roomGameState]);

  // Socket event listeners
  useEffect(() => {
    const handleQuestion = (data: DrinkingGameQuestion) => {
      if (data.isDrinkingGame) {
        console.log('🍺 Drinking Game question received:', data);
        setCurrentQuestion(data);
        currentQuestionRef.current = data;
        setTimeRemaining(data.timeLimit);
        resetQuestion();
        const questionId = data.questionId || data.id;
        previousQuestionIdRef.current = questionId;
        currentQuestionIdRef.current = questionId;
      }
    };

    const handleQuestionResult = (data: QuestionResultPayload) => {
      // Don't switch screens - just change to drinking phase
      // The flip card stays visible, just the message changes
      const resolved = resolveSipResults(
        data,
        playersRef.current,
        currentQuestionRef.current?.sipPenalty || 1
      );
      console.log('🍺 Question result received - starting drinking time', {
        sipResultsFromServer: data?.sipResults?.length ?? 0,
        resolvedDrinkers: resolved.length,
      });
      setSipResults(resolved);
      setWaitingPhase('drinking');
      
      // After 6 seconds of drinking time, switch to loading phase
      setTimeout(() => {
        setWaitingPhase('loading');
      }, 6000);
    };

    const handleGameEnded = (data: { leaderboard: Array<{ playerId: string; playerName: string; score: number }> }) => {
      setGameEnded(true);
      setFinalLeaderboard(data.leaderboard);
      setCurrentQuestion(null);
    };

    const handleAnswerReceived = (data: {
      questionId: string;
      isCorrect: boolean;
      points: number;
      sipsEarned?: number;
      correctAnswer?: string;
    }) => {
      setAnswerCorrect(data.isCorrect);
      setPointsEarned(data.points);
      // Use sipsEarned from backend, or fall back to current question's sipPenalty if wrong
      const sips = data.sipsEarned ?? (data.isCorrect ? 0 : (currentQuestionRef.current?.sipPenalty || 1));
      setSipsEarned(sips);
      setAnswerSubmitted(true);
      setWaitingPhase('waiting');

      // Show interesting fact after 1.5 seconds (flip animation)
      const answeredQuestionId = currentQuestionIdRef.current;
      setShowInterestingFact(false);
      setTimeout(() => {
        if (currentQuestionIdRef.current === answeredQuestionId) {
          setShowInterestingFact(true);
        }
      }, 1500);
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('answer-received', handleAnswerReceived);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('answer-received', handleAnswerReceived);
    };
  }, [socket]);

  // Timer countdown
  useEffect(() => {
    if (!currentQuestion) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 100) {
          clearInterval(interval);
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentQuestion]);

  // Handle answer submission
  const handleAnswerClick = (answer: string) => {
    if (answerSubmitted || !currentQuestion || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setSelectedAnswer(answer);

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      console.error('No question ID available');
      isSubmittingRef.current = false;
      return;
    }

    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId,
      answer,
      timestamp: Date.now(),
    });
  };

  // Get the fun fact from current question
  const funFact = currentQuestion?.interesting || currentQuestion?.funFact;

  // Game ended screen
  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        resetQuestion();
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    const displayLeaderboard =
      finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? []);

    return (
      <GameEndedScreen
        gameType={roomState.gameType}
        finalLeaderboard={displayLeaderboard}
        myPlayerId={myPlayerId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        showYouSuffix
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  // Waiting state - no question yet
  if (!currentQuestion) {
    return (
      <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
        <Header
          leftButton={getPlayerHeaderLeftButton({
              isHost,
              onBackToGameSelection,
              onGoHome,
              backTooltip: t('gameplay.backToGameSelection'),
              homeTooltip: t('common.tooltips.home'),
            })}
          rightButton={{
            icon: HeaderIcons.refresh,
            onClick: () => window.location.reload(),
            tooltip: t('common.tooltips.refreshPage')
          }}
          roomCode={roomState.roomName}
        />
        <div className="player-display" style={{ width: '100%', maxWidth: '100%' }}>
          <WaitingSpinner
            message={t('gameplay.partyHost.waitingDrinking')}
            isLoading={true}
            color={THEME.secondary}
          />
        </div>
      </div>
    );
  }

  // Question display
  const progress = (timeRemaining / currentQuestion.timeLimit) * 100;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);
  const isLowTime = secondsRemaining <= 5;

  // Render the status indicator below the flip card
  const renderStatusIndicator = () => {
    if (waitingPhase === 'waiting') {
      return <WaitingSpinner isLoading={false} color={THEME.secondary} />;
    }
    
    if (waitingPhase === 'drinking') {
      // When host plays there is no cast screen — show who drinks here.
      // Also show whenever we have sipResults (covers hostIsPlaying detection edge cases).
      const showWhoDrinks = hostIsPlaying || sipResults.length > 0;
      if (showWhoDrinks) {
        let drinkers = sipResults;
        // Last-resort: at least show this player's own sips if server list was empty
        if (drinkers.length === 0 && sipsEarned > 0) {
          const me = roomState.players.find((p) => p.id === myPlayerId);
          drinkers = [{
            playerId: myPlayerId,
            playerName: me?.name || 'You',
            avatar: me?.avatar,
            sips: sipsEarned,
          }];
        }
        const hasDrinkers = drinkers.length > 0;
        return (
          <div
            data-testid="drinking-who-drinks"
            style={{
              marginTop: '1rem',
              background: hasDrinkers ? THEME.primaryLight : 'rgba(52, 211, 153, 0.12)',
              border: `2px solid ${hasDrinkers ? THEME.primaryBorder : 'rgba(52, 211, 153, 0.35)'}`,
              borderRadius: '1rem',
              padding: '1rem',
            }}
          >
            <h2
              style={{
                textAlign: 'center',
                margin: '0 0 0.75rem',
                color: hasDrinkers ? THEME.primary : '#34D399',
                fontSize: '1.2rem',
                fontWeight: 700,
              }}
            >
              {hasDrinkers
                ? `🍺 ${t('gameplay.hotTakes.drinkingTime')} 🍺`
                : '🎉 Nobody Drinks! 🎉'}
            </h2>
            {hasDrinkers ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {drinkers.map((entry) => (
                  <div
                    key={entry.playerId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.65rem',
                      padding: '0.7rem 0.8rem',
                      background: 'rgba(0,0,0,0.35)',
                      borderRadius: '0.75rem',
                    }}
                  >
                    {renderAvatar(entry.avatar, '1.75rem')}
                    <span
                      style={{
                        flex: 1,
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#fff',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.playerName}
                      {entry.playerId === myPlayerId ? ` ${t('common.you')}` : ''}
                    </span>
                    <span
                      style={{
                        flexShrink: 0,
                        background: THEME.primary,
                        color: '#fff',
                        padding: '0.35rem 0.65rem',
                        borderRadius: '0.5rem',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                      }}
                    >
                      🍺 {entry.sips} {entry.sips === 1 ? t('common.sip_one') : t('common.sip_other')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', fontSize: '0.95rem', color: '#fff', opacity: 0.9 }}>
                Everyone got it right! 🎊
              </div>
            )}
          </div>
        );
      }

      return (
        <div style={{ 
          marginTop: '1.5rem', 
          textAlign: 'center',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            color: THEME.primary,
            marginBottom: '0.5rem',
          }}>
            <span className="drink-bounce" style={{ animation: 'drinkBounce 0.5s ease-in-out infinite' }}>🍺</span>
            <span>{t('gameplay.hotTakes.drinkingTime')}</span>
            <span className="drink-bounce" style={{ animation: 'drinkBounce 0.5s ease-in-out infinite 0.25s' }}>🍻</span>
          </div>
          <div style={{ 
            fontSize: '0.9rem', 
            color: 'rgba(255,255,255,0.75)',
          }}>
            {t('gameplay.hotTakes.checkHostScreen')}
          </div>
        </div>
      );
    }
    
    // Loading phase – same wrapper/position as waiting for others
    return <WaitingSpinner isLoading={true} message={t('gameplay.loading.nextQuestion')} color={THEME.secondary} />;
  };

  return (
    <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
      <Header
        leftButton={getPlayerHeaderLeftButton({
            isHost,
            onBackToGameSelection,
            onGoHome,
            backTooltip: t('gameplay.backToGameSelection'),
            homeTooltip: t('common.tooltips.home'),
          })}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage')
        }}
        roomCode={roomState.roomName}
      />
      <div className="game-player-wrapper">
        <div className="game-player-content">
          {/* Sip Penalty (no timer counter on player - avoids sync mismatch with host) */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{
              background: THEME.primaryLight,
              padding: '0.2rem 0.6rem',
              borderRadius: '1rem',
              color: THEME.primary,
              fontSize: '0.8rem',
            }}>
              🍺 {currentQuestion.sipPenalty || 1} {(currentQuestion.sipPenalty || 1) === 1 ? 'sip' : 'sips'}
            </span>
          </div>

          {/* Timer Bar */}
          <div style={{
            width: '100%',
            height: '10px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '5px',
            overflow: 'hidden',
            opacity: answerSubmitted ? 0.3 : 1,
          }}>
            <div
              className={isLowTime ? 'timer-bar__slider-fill--warning' : ''}
              style={{
                width: `${progress}%`,
                height: '100%',
                background: isLowTime ? '#EF4444' : '#34D399',
                transition: 'width 0.1s linear',
              }}
            />
          </div>

          {/* Conditional Question Display - only shown when host is playing */}
          {hostIsPlaying && (
            <div
              className="player-dark-panel"
              style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '1rem',
              padding: '1.5rem',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: '1.2rem',
                lineHeight: '1.5',
                fontWeight: '500',
                color: 'white',
              }}>
                {currentQuestion.question}
              </div>
            </div>
          )}

          {/* Answer Options */}
          {!answerSubmitted ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}>
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerClick(option)}
                  className={`player-dark-option${selectedAnswer === option ? ' player-dark-option--selected' : ''}`}
                  style={{
                    padding: '1rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderRadius: '0.75rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: selectedAnswer === option ? `3px solid ${THEME.primary}` : '3px solid rgba(255,255,255,0.2)',
                    background: selectedAnswer === option ? THEME.primaryLight : 'rgba(255,255,255,0.05)',
                    color: 'white',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ marginRight: '0.75rem', opacity: 0.6 }}>
                    {String.fromCharCode(65 + index)}.
                  </span>
                  {option}
                </button>
              ))}
            </div>
          ) : (
            // Flip card showing result, then "Did you know" after 1.5s
            <div style={{
              marginTop: '1rem',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
            }}>
              {(() => {
                const didYouKnowFlipped = !!(showInterestingFact && funFact);
                return (
              <div style={{
                position: 'relative',
                width: '100%',
                minHeight: funFact ? '150px' : 'auto',
                transformStyle: 'preserve-3d',
                transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: didYouKnowFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              }}>
                {/* Front - Result (Correct or Wrong) */}
                <div style={{
                  position: didYouKnowFlipped ? 'absolute' : 'relative',
                  top: didYouKnowFlipped ? 0 : undefined,
                  left: didYouKnowFlipped ? 0 : undefined,
                  right: didYouKnowFlipped ? 0 : undefined,
                  width: '100%',
                  padding: '2rem 1.5rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  border: `3px solid ${answerCorrect ? 'rgba(52, 211, 153, 0.5)' : THEME.primaryBorder}`,
                  borderRadius: '1rem',
                  textAlign: 'center',
                  color: 'var(--text-primary)',
                  boxShadow: 'var(--shadow-md)',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  boxSizing: 'border-box',
                }}>
                  {answerCorrect ? (
                    <>
                      <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🎉</div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#34D399' }}>
                        Correct! +{pointsEarned} points
                      </div>
                      <div style={{ fontSize: '0.95rem', opacity: 0.8, marginTop: '0.5rem' }}>
                        No drinking for you this round!
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🍺</div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: THEME.primary }}>
                        {t('gameplay.drinkingGame.wrongAnswerSips', {
                          count: sipsEarned,
                          sipLabel: sipsEarned === 1 ? t('common.sip_one') : t('common.sip_other'),
                        })}
                      </div>
                    </>
                  )}
                </div>

                {/* Back - Did you know */}
                {funFact && (
                  <div style={{
                    position: didYouKnowFlipped ? 'relative' : 'absolute',
                    top: didYouKnowFlipped ? undefined : 0,
                    left: didYouKnowFlipped ? undefined : 0,
                    right: didYouKnowFlipped ? undefined : 0,
                    width: '100%',
                    padding: '2rem 1.5rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: `3px solid ${answerCorrect ? 'rgba(52, 211, 153, 0.5)' : THEME.primaryBorder}`,
                    borderRadius: '1rem',
                    textAlign: 'center',
                    color: 'var(--text-primary)',
                    boxShadow: 'var(--shadow-md)',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                    boxSizing: 'border-box',
                  }}>
                    <p style={{
                      fontSize: '1.1rem',
                      color: THEME.accent,
                      fontWeight: 'bold',
                      margin: '0 0 0.8rem 0',
                    }}>
                      💡 Did you know?
                    </p>
                    <p style={{
                      fontSize: '1rem',
                      color: 'var(--text-primary)',
                      lineHeight: '1.6',
                      margin: 0,
                    }}>
                      {funFact}
                    </p>
                  </div>
                )}
              </div>
                );
              })()}

              {/* Status indicator - changes based on phase */}
              {renderStatusIndicator()}
              {currentQuestion && (
                <div style={{ width: '100%', marginTop: '0.5rem' }}>
                  <QuestionFeedbackSection
                    questionId={currentQuestion.questionId || currentQuestion.id || ''}
                    roomName={roomState.roomName}
                    gameType={roomState.gameType}
                    answerSubmitted={answerSubmitted}
                    showFeedback={true}
                    onSubmitFeedback={(rating, correct, questionText, correctAnswerVal) => {
                      if ((rating !== undefined || correct !== undefined) && socket) {
                        socket.emit('question-feedback', {
                          roomName: roomState.roomName,
                          questionId: currentQuestion.questionId || currentQuestion.id,
                          gameType: roomState.gameType,
                          rating: rating ?? undefined,
                          correct: correct ?? undefined,
                          questionText: questionText ?? currentQuestion.question,
                          correctAnswer: correctAnswerVal ?? currentQuestion.correctAnswer,
                        });
                      }
                    }}
                    questionText={currentQuestion.question}
                    correctAnswer={currentQuestion.correctAnswer}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        @keyframes drinkBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DrinkingGamePlayerDisplay;
